// The extra-costs cell: up to three typed rows the manager adds and removes
// in place, and — for a document stored in the old shape — the service lines it
// already carries, printed read-only at the amount its total actually contains.
//
// INVARIANT: no form hook runs on the printed path. The customer's page mounts
// this cell with no form around it at all.
import type { ReactNode } from 'react';
import { useFieldArray, useFormContext, useWatch, type FieldArrayPath } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  LOGISTICS_CALCULATION_MAX_EXTRA_COSTS,
  type LogisticsCalculationEditableContent,
} from '@magimp/shared';
import { DocButton, useDocumentEditing, type DocumentPath } from './DocumentCell.js';
import { CostCell, CostDash, Money } from './DocumentCost.js';
import type { CargoRowAmounts, RenderedLine } from './sheet-amounts.js';

type Form = LogisticsCalculationEditableContent;

export function ExtrasCell({
  cargoIndex,
  column,
  amounts,
}: {
  cargoIndex: number;
  column: string;
  amounts: CargoRowAmounts;
}) {
  const editing = useDocumentEditing();
  const path = `cargos.${cargoIndex}`;
  const printedExtras = amounts.extras.filter((extra) => extra.amount !== '');
  return (
    <>
      {editing ? (
        <EditableExtras path={`${path}.extras`} column={column} />
      ) : (
        printedExtras.map((extra, index) => (
          <span className="doc-xtr" key={index}>
            <Money amount={extra.amount} currency={extra.currency} />
            {extra.caption ? <span className="doc-sm">{extra.caption}</span> : null}
          </span>
        ))
      )}
      <LegacyLines path={`${path}.lines`} lines={amounts.legacy} />
      {!editing && printedExtras.length === 0 && amounts.legacy.length === 0 ? <CostDash /> : null}
    </>
  );
}

/** Stored service lines of one group — a cargo's own, or the batch-wide ones. */
export function LegacyLines({ path, lines }: { path: string; lines: readonly RenderedLine[] }) {
  const editing = useDocumentEditing();
  if (!editing) {
    return (
      <>
        {lines.map((line) => (
          <LegacyRow key={line.index} line={line} action={null} />
        ))}
      </>
    );
  }
  return <EditableLegacyLines path={path} lines={lines} />;
}

/**
 * INVARIANT: the row count comes from `useFieldArray`, never from `useWatch`.
 * The two settle on different renders after an add or a remove, and a row
 * rendered for an index the array no longer has re-registers its controls at
 * that index — writing an empty cost back into the form.
 */
function EditableExtras({ path, column }: { path: string; column: string }) {
  const { t } = useTranslation('logisticsCalculations');
  const { control } = useFormContext<Form>();
  const array = useFieldArray({ control, name: path as FieldArrayPath<Form> });
  return (
    <>
      {array.fields.map((field, index) => (
        <span className="doc-xtr" key={field.id}>
          <CostCell
            name={`${path}.${index}` as DocumentPath}
            column={`${column} ${index + 1}`}
            defaultCurrency="USD"
            cost={null}
            captioned
            hint={t('editor.cost.hints.extra')}
          />
          <DocButton
            label={t('editor.cost.removeExtra', { index: index + 1 })}
            danger
            onClick={() => array.remove(index)}
          />
        </span>
      ))}
      {array.fields.length < LOGISTICS_CALCULATION_MAX_EXTRA_COSTS ? (
        <DocButton
          label={t('editor.cost.addExtra')}
          onClick={() => array.append({ amount: '', currency: 'USD' } as never)}
        />
      ) : null}
    </>
  );
}

/**
 * Stored service lines, read-only in both modes but removable while editing.
 *
 * WHY removable: draft validation accepts a line with an empty quantity or rate
 * and publication does not, so retiring the line editor without leaving removal
 * would strand those drafts with no escape but deleting the whole cargo.
 */
function EditableLegacyLines({ path, lines }: { path: string; lines: readonly RenderedLine[] }) {
  const { t } = useTranslation('logisticsCalculations');
  const { control, getValues, setValue } = useFormContext<Form>();
  const stored = (useWatch({ control, name: path as DocumentPath }) ?? []) as unknown[];
  const remove = (index: number) =>
    setValue(
      path as DocumentPath,
      (getValues(path as DocumentPath) as unknown[]).filter(
        (_row, position) => position !== index,
      ) as never,
      { shouldDirty: true, shouldValidate: true },
    );
  return (
    <>
      {lines.slice(0, stored.length).map((line) => (
        <LegacyRow
          key={line.index}
          line={line}
          action={
            <DocButton
              label={t('editor.cost.removeLine')}
              danger
              onClick={() => remove(line.index)}
            />
          }
        />
      ))}
    </>
  );
}

function LegacyRow({ line, action }: { line: RenderedLine; action: ReactNode }) {
  return (
    <span className="doc-xtr" data-testid="rl-document-legacy-line">
      {line.currency ? (
        <Money amount={line.amount} currency={line.currency} />
      ) : (
        <span className="doc-n">{line.amount}</span>
      )}
      <span className="doc-sm">
        {line.faceCaption ? `${line.serviceName} · ${line.faceCaption}` : line.serviceName}
      </span>
      {action}
    </span>
  );
}
